export const CHECKOUT_URL = 'https://veneer-en.impultienda.ar/checkout/';

export const MOCKUP_URL =
  'https://cdn.magicpatterns.com/uploads/8UwcdYdxvtBwEey4mbUbhh/MOCKUP_VENEER-EN.png';
export const AUTHOR_URL =
  'https://cdn.magicpatterns.com/uploads/oduPXN7jx9XThMP5LLbkBx/Dr._Sarah_Collins_-_DENTAL.png';

export const heroChecks = [
  '6 Practical Clinical Volumes',
  '5 Ready-to-Use Clinical Bonuses',
  'Immediate Digital Access',
  '7-Day Money-Back Guarantee',
];

export const scenarios = [
  { title: 'A fractured veneer', text: 'A patient returns with a fractured veneer.' },
  { title: 'Persistent sensitivity', text: 'Another reports persistent sensitivity after placement.' },
  {
    title: 'Discoloration or debonding',
    text: 'A different case involves marginal discoloration, debonding, or an aesthetic outcome that no longer meets expectations.',
  },
];

export const considerations = [
  'The patient’s symptoms and clinical history.',
  'The condition of the existing restoration.',
  'Possible bonding or structural concerns.',
  'Marginal integrity and aesthetic discrepancies.',
  'Relevant biological and functional findings.',
  'Whether additional assessment, referral, repair, replacement, or another management pathway warrants consideration.',
];

export const beforeItems = [
  'Assessment findings recorded without a consistent sequence.',
  'Difficulty separating aesthetic, structural, bonding, and biological concerns.',
  'Corrective options considered before the assessment is fully organized.',
  'Inconsistent documentation between complication cases.',
  'Unclear records of why particular management options were considered.',
  'Repeated effort rebuilding assessment checklists from scratch.',
];

export const afterItems = [
  'Follow a repeatable five-stage assessment framework.',
  'Organize complications into clearly defined evaluation categories.',
  'Document findings and unresolved clinical questions.',
  'Compare corrective planning options using structured decision worksheets.',
  'Create a case-specific planning record that captures clinical reasoning.',
  'Reuse practical templates across different veneer complication scenarios.',
];

export const steps = [
  {
    name: 'ASSESS',
    title: 'Build the Initial Clinical Picture',
    text: 'Organize the patient’s concerns, relevant history, restoration details, examination findings, and available records.',
    list: [] as string[],
    note: '',
    output: 'Initial Veneer Complication Assessment Sheet',
  },
  {
    name: 'CLASSIFY',
    title: 'Identify the Primary Complication Category',
    text: 'Organize observed findings into relevant categories, including:',
    list: [
      'Bonding and retention concerns.',
      'Structural fractures and defects.',
      'Marginal and aesthetic discrepancies.',
      'Sensitivity and discomfort.',
      'Potential biological or functional contributors.',
    ],
    note: 'Multiple categories may apply to the same case.',
    output: 'Veneer Complication Classification Record',
  },
  {
    name: 'EVALUATE',
    title: 'Identify the Clinical Questions That Matter',
    text: 'Use structured decision prompts to organize further assessment considerations, unresolved findings, risk factors, and circumstances requiring additional professional evaluation.',
    list: [],
    note: '',
    output: 'Clinical Decision Evaluation Worksheet',
  },
  {
    name: 'PLAN',
    title: 'Structure the Corrective Management Discussion',
    text: 'Organize the clinical considerations associated with possible management pathways. These may include monitoring, further investigation, repair, replacement, referral, or other professionally appropriate options.',
    list: [],
    note: 'The framework does not automatically select a treatment.',
    output: 'Corrective Planning Decision Record',
  },
  {
    name: 'DOCUMENT',
    title: 'Turn Your Assessment Into a Clear Professional Record',
    text: 'Bring together clinical findings, relevant considerations, unresolved questions, proposed next steps, and follow-up planning.',
    list: [],
    note: '',
    output: 'Completed Veneer Complication Case Planning Template',
  },
];

export const volumes = [
  {
    number: '01',
    title: 'Veneer Complication Initial Assessment Blueprint™',
    tagline: 'Organize the First Stage of Every Complication Case.',
    description:
      'A practical starting framework for collecting relevant clinical information before moving into corrective planning.',
    items: [
      'Initial Complication Intake Workflow.',
      'Patient Concern Documentation Template.',
      'Existing Restoration Assessment Checklist.',
      'Clinical History and Symptom Review Worksheet.',
      'Preliminary Findings Organization Map.',
      'Additional Assessment and Referral Considerations Checklist.',
    ],
    deliverable:
      'A structured initial assessment record that organizes the information available at the beginning of a veneer complication case.',
    note: 'Use it when a patient presents with an existing veneer concern and the clinical picture needs to be documented systematically.',
  },
  {
    number: '02',
    title: 'Veneer Bonding Failure Decision Blueprint™',
    tagline: 'Structure the Assessment of Debonding and Retention Problems.',
    description:
      'A focused workflow for organizing findings associated with veneer debonding, retention concerns, and possible adhesive-interface problems.',
    items: [
      'Veneer Debonding Assessment Pathway.',
      'Bonding Failure Findings Checklist.',
      'Restoration and Substrate Evaluation Worksheet.',
      'Relevant Functional and Occlusal Considerations Map.',
      'Further Investigation Decision Prompts.',
      'Repair, Replacement, and Referral Planning Considerations Matrix.',
    ],
    deliverable:
      'A documented bonding-related complication assessment with an organized record of corrective options requiring clinical evaluation.',
    note: 'Built to support case reasoning rather than prescribe a universal rebonding technique.',
  },
  {
    number: '03',
    title: 'Veneer Fracture & Structural Failure Blueprint™',
    tagline: 'Organize Fracture Findings Before Planning the Next Step.',
    description:
      'A practical assessment pathway for veneer fractures, chipping, structural defects, and related restoration concerns.',
    items: [
      'Veneer Fracture Assessment Checklist.',
      'Structural Defect Classification Worksheet.',
      'Restoration Integrity Evaluation Map.',
      'Functional and Occlusal Findings Documentation Tool.',
      'Contributing Factor Review Checklist.',
      'Corrective Planning Options Worksheet.',
    ],
    deliverable:
      'A structured fracture assessment and corrective planning record that connects documented findings with relevant clinical considerations.',
    note: 'Use it to organize the evaluation of structural complications without assuming that every fracture requires the same management approach.',
  },
  {
    number: '04',
    title: 'Veneer Aesthetic & Marginal Complications Blueprint™',
    tagline: 'Turn Aesthetic Complaints Into a Structured Evaluation Record.',
    description:
      'A practical framework for organizing concerns involving veneer appearance, marginal discrepancies, contour, shade, and restoration integration.',
    items: [
      'Aesthetic Complaint Assessment Workflow.',
      'Shade and Appearance Documentation Checklist.',
      'Marginal Discrepancy Evaluation Worksheet.',
      'Contour and Surface Findings Record.',
      'Patient Expectation Documentation Template.',
      'Aesthetic Corrective Planning Considerations Map.',
    ],
    deliverable:
      'A documented aesthetic and marginal assessment that separates patient concerns, observed findings, and corrective planning considerations.',
    note: 'Designed to support clear clinical records and professional treatment discussions.',
  },
  {
    number: '05',
    title: 'Post-Veneer Sensitivity & Discomfort Assessment Blueprint™',
    tagline: 'Organize Patient Symptoms Without Jumping to Conclusions.',
    description:
      'A structured workflow for documenting sensitivity, discomfort, and other patient-reported concerns following veneer treatment.',
    items: [
      'Post-Veneer Symptom Intake Checklist.',
      'Sensitivity History Documentation Tool.',
      'Relevant Clinical Findings Worksheet.',
      'Potential Contributing Factor Review Map.',
      'Further Assessment and Referral Considerations Checklist.',
      'Follow-Up Documentation Template.',
    ],
    deliverable:
      'A structured symptom assessment record that helps organize findings, clinical questions, and professionally determined next steps.',
    note: 'This volume does not substitute for diagnosis, urgent assessment when indicated, or individualized clinical care.',
  },
  {
    number: '06',
    title: 'Veneer Corrective Planning & Documentation Roadmap™',
    tagline: 'Bring Every Finding Together Into One Organized Case Plan.',
    description:
      'The final volume connects the previous assessment workflows into a consolidated professional planning and documentation system.',
    items: [
      'Corrective Planning Workflow.',
      'Clinical Findings Consolidation Template.',
      'Management Options Comparison Worksheet.',
      'Patient Communication Documentation Checklist.',
      'Follow-Up Planning Record.',
      'Complete Veneer Complication Case Summary Template.',
    ],
    deliverable:
      'A completed corrective planning record containing the assessment summary, documented considerations, proposed next steps, and follow-up plan.',
    note: 'The goal is not to automate clinical treatment decisions. It is to give those decisions a clearer documentation structure.',
  },
];

export const bonuses = [
  {
    number: '01',
    title: 'Veneer Complication Quick-Reference Decision Cards™',
    tagline: 'Keep the Five-Stage Framework Within Reach.',
    description:
      'A compact collection of reference cards covering the main assessment stages and complication categories.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/1b94bdf0-b3f1-462d-832a-30678b7fb9af.jpg',
    items: [
      'Initial Assessment Reference Card.',
      'Bonding Concern Reference Card.',
      'Structural Complication Reference Card.',
      'Aesthetic and Marginal Concern Reference Card.',
      'Sensitivity and Discomfort Reference Card.',
    ],
    benefit:
      'Quickly identify the relevant assessment workflow and documentation tool for the case in front of you.',
    note: '',
  },
  {
    number: '02',
    title: 'Veneer Clinical Photography & Documentation Checklist™',
    tagline: 'Create a More Consistent Visual Documentation Workflow.',
    description:
      'A practical checklist for organizing clinical photography and associated case records.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/dae68e66-2641-4cb0-9b91-c38e37b4175d.jpg',
    items: [
      'Pre-Assessment Photography Checklist.',
      'Restoration Findings Documentation Guide.',
      'Aesthetic Concern Recording Template.',
      'Image Organization Worksheet.',
      'Follow-Up Comparison Documentation Checklist.',
    ],
    benefit:
      'Support consistent photographic documentation while following applicable consent and privacy requirements.',
    note: '',
  },
  {
    number: '03',
    title: 'Veneer Patient Communication & Consent Worksheets™',
    tagline: 'Organize Important Clinical Discussions.',
    description:
      'A collection of professional documentation worksheets for recording discussions about findings, uncertainties, management considerations, and next steps.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/d4d6b469-8642-4f2e-a445-a0293584134e.jpg',
    items: [
      'Patient Concern Summary Template.',
      'Clinical Findings Discussion Checklist.',
      'Corrective Options Conversation Worksheet.',
      'Questions and Expectations Record.',
      'Follow-Up Communication Checklist.',
    ],
    benefit:
      'Create a structured record of patient discussions that can be adapted to professional and local consent requirements.',
    note: 'These worksheets are not substitutes for legally required consent forms.',
  },
  {
    number: '04',
    title: 'Veneer Complication Case Audit Checklist™',
    tagline: 'Review Your Documentation Before Closing the Planning Record.',
    description: 'A practical quality-review tool focused on documentation completeness.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/456668f6-b765-40e6-a318-cc0e4baf594e.jpg',
    items: [
      'Assessment Completeness Checklist.',
      'Complication Classification Review.',
      'Unresolved Findings Review.',
      'Corrective Planning Documentation Check.',
      'Follow-Up Record Verification.',
    ],
    benefit:
      'Review whether the relevant sections of the case record have been completed before finalizing the documentation.',
    note: '',
  },
  {
    number: '05',
    title: 'Veneer Complication Master Case Tracker™',
    tagline: 'Keep Multiple Complication Cases Organized.',
    description:
      'A reusable case-tracking template designed to help professionals organize assessment status, documentation progress, and planned follow-up.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/fb1da3d3-579e-4e43-a41d-d0e1642ba8e2.jpg',
    items: [
      'Case Reference Fields.',
      'Complication Category Tracker.',
      'Assessment Completion Status.',
      'Planning Documentation Status.',
      'Follow-Up Tracking Fields.',
    ],
    benefit:
      'Maintain an organized overview of professional workflow progress across multiple veneer complication cases.',
    note: 'Use only within appropriate secure systems and in accordance with patient confidentiality requirements.',
  },
];

export const workflowSteps = [
  {
    label: 'FIRST: Document the presenting concern.',
    tool: 'Open Volume 01.',
    text: 'Record the patient’s complaint, relevant treatment history, restoration information, symptoms, and initial findings.',
  },
  {
    label: 'NEXT: Organize the complication category.',
    tool: 'Open Volume 03.',
    text: 'Document the fracture characteristics, restoration condition, and relevant functional observations. If symptoms require additional assessment, use the appropriate worksheets from Volume 05.',
  },
  {
    label: 'THEN: Review the unresolved clinical questions.',
    tool: 'Use the Veneer Complication Decision Map™.',
    text: 'Identify which findings have been established, which remain uncertain, and what further professional assessment may be appropriate.',
  },
  {
    label: 'NOW: Structure the corrective planning record.',
    tool: 'Open Volume 06.',
    text: 'Document the management options being considered, the relevant clinical factors, and the reasoning behind the proposed next steps.',
  },
  {
    label: 'FINALLY: Complete the case documentation.',
    tool: 'Use the Master Case Tracker and Case Audit Checklist.',
    text: 'Organize the assessment record, communication notes, and follow-up information.',
  },
];

export const audiences = [
  {
    title: 'Cosmetic Dentists',
    text: 'Professionals involved in veneer treatment and the evaluation of aesthetic restoration concerns.',
  },
  {
    title: 'Restorative Dentists',
    text: 'Clinicians assessing existing restorations, structural concerns, and corrective planning considerations.',
  },
  {
    title: 'Dental Professionals Involved in Veneer Treatment Planning',
    text: 'Qualified professionals seeking practical documentation templates and structured case-assessment workflows within their permitted scope of practice.',
  },
];

export const testimonials = [
  {
    name: 'Dr. Laura Méndez',
    role: 'Cosmetic Dentist · Madrid, Spain',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/20cea063-0d9a-4d09-a5dd-0fe502711d22.jpg',
    quote:
      'I used to keep complication notes in three different places. Now every veneer case follows the same five stages, and my records finally read like a clear line of reasoning instead of scattered observations.',
  },
  {
    name: 'Dr. Carlos Rivera',
    role: 'Restorative Dentist · Bogotá, Colombia',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/a1c3feb1-6107-4d78-a335-4a943eca3a1f.jpg',
    quote:
      'The classification record alone was worth it. Separating bonding, structural and aesthetic findings before talking options with the patient made my planning discussions far more organized.',
  },
  {
    name: 'Dr. Valentina Ortiz',
    role: 'General & Cosmetic Dentist · Santiago, Chile',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/4abcbe31-6a45-4ed1-bc1b-7c85c30c372f.jpg',
    quote:
      'The sensitivity volume stopped me from jumping to conclusions. I document symptoms, findings and open questions first — and the follow-up template keeps everything in one place.',
  },
  {
    name: 'Dr. Ricardo Salinas',
    role: 'Prosthodontist · Monterrey, Mexico',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/017fcc1c-594b-4d08-a885-5fb0de55bc39.jpg',
    quote:
      'After 25 years in practice I didn’t expect a $27 resource to change my workflow. The Master Case Tracker and Audit Checklist now sit at the end of every complication case I close.',
  },
];

export const coreVolumesList = [
  'Volume 01 — Veneer Complication Initial Assessment Blueprint™',
  'Volume 02 — Veneer Bonding Failure Decision Blueprint™',
  'Volume 03 — Veneer Fracture & Structural Failure Blueprint™',
  'Volume 04 — Veneer Aesthetic & Marginal Complications Blueprint™',
  'Volume 05 — Post-Veneer Sensitivity & Discomfort Assessment Blueprint™',
  'Volume 06 — Veneer Corrective Planning & Documentation Roadmap™',
];

export const bonusList = [
  'Veneer Complication Quick-Reference Decision Cards™',
  'Veneer Clinical Photography & Documentation Checklist™',
  'Veneer Patient Communication & Consent Worksheets™',
  'Veneer Complication Case Audit Checklist™',
  'Veneer Complication Master Case Tracker™',
];

export const faqs = [
  {
    q: 'Is this designed for cosmetic or restorative dentists?',
    a: 'Yes. The collection is designed primarily for qualified cosmetic and restorative dental professionals involved in veneer assessment and treatment planning. Other dental professionals may use the educational materials within their appropriate professional scope.',
  },
  {
    q: 'Is this a theoretical dental textbook?',
    a: 'No. The primary focus is on practical assessment workflows, decision maps, documentation worksheets, checklists, and corrective planning templates.',
  },
  {
    q: 'Does the blueprint provide a specific treatment for every veneer complication?',
    a: 'No. The blueprint provides a structured framework for organizing clinical findings and management considerations. Treatment decisions require individualized assessment, current clinical evidence, and professional judgment.',
  },
  {
    q: 'What types of complications are covered?',
    a: 'The collection addresses assessment and documentation workflows related to bonding failures, fractures, structural concerns, aesthetic discrepancies, marginal problems, sensitivity, and other relevant restoration concerns.',
  },
  {
    q: 'Can I use these tools with my own clinical cases?',
    a: 'The templates are designed to support professional case organization and documentation. They should be adapted to the clinician’s scope of practice, clinical environment, documentation requirements, and applicable standards.',
  },
  {
    q: 'Will I receive physical books?',
    a: 'No. This is a digital product containing six practical volumes and five complementary resources.',
  },
  {
    q: 'How will I receive access?',
    a: 'Digital access is provided after successful purchase.',
  },
  {
    q: 'Is this a subscription?',
    a: 'No. The $27 purchase is a one-time payment for the included digital collection.',
  },
  {
    q: 'Is there a money-back guarantee?',
    a: 'Yes. The product includes a seven-day money-back guarantee.',
  },
  {
    q: 'Does this replace clinical training or professional judgment?',
    a: 'No. This resource is a professional educational and workflow-support tool. It does not replace clinical examination, diagnosis, supervised training, professional licensure, individualized treatment planning, or appropriate referral.',
  },
];

export const buyers = [
  { name: 'María G.', country: 'México' },
  { name: 'Carlos R.', country: 'Colombia' },
  { name: 'Lucía F.', country: 'Argentina' },
  { name: 'Javier M.', country: 'España' },
  { name: 'Valentina P.', country: 'Chile' },
  { name: 'Andrés T.', country: 'Perú' },
  { name: 'Camila S.', country: 'Uruguay' },
  { name: 'Diego L.', country: 'Ecuador' },
  { name: 'Sofía H.', country: 'Costa Rica' },
  { name: 'Mateo V.', country: 'Guatemala' },
  { name: 'Daniela C.', country: 'Venezuela' },
  { name: 'Fernando Q.', country: 'Bolivia' },
  { name: 'Paula N.', country: 'Paraguay' },
  { name: 'Ricardo A.', country: 'República Dominicana' },
  { name: 'Gabriela D.', country: 'Panamá' },
  { name: 'Alejandro B.', country: 'Puerto Rico' },
];
